// src/lib/consoleGuard.js
//
// Silences ALL console output by default in production builds, so the deployed
// site leaks no internal state (API endpoints, user ids, agent flow traces,
// feature flags) to anyone who opens devtools. An admin can turn logging back
// on from /admin/settings -> feature flag `consoleLogsEnabled`; the flag is
// read once per page load by applyConsolePref() below.
//
// ponytail: no live Firestore listener and no per-env override knob — the flag
// takes effect on the next page load, same as every other feature flag here.
// Dev builds (`npm start`) keep their console untouched; the leak only matters
// on the deployed build.

const METHODS = [
  'log', 'debug', 'info', 'warn', 'error', 'trace', 'dir', 'dirxml',
  'table', 'group', 'groupCollapsed', 'groupEnd', 'count', 'countReset',
  'time', 'timeEnd', 'timeLog', 'assert',
];

const noop = () => {};
const original = {};
let silenced = false;

function silence() {
  if (silenced) return;
  METHODS.forEach((m) => {
    if (typeof console[m] === 'function') {
      original[m] = console[m].bind(console);
      console[m] = noop;
    }
  });
  silenced = true;
}

function restore() {
  if (!silenced) return;
  Object.keys(original).forEach((m) => { console[m] = original[m]; });
  silenced = false;
}

// Default state: silent in production, verbatim everywhere else.
if (process.env.NODE_ENV === 'production') {
  silence();
}

// Reads appConfig/global once; if flags.consoleLogsEnabled === true, restores
// the real console. Firestore rules require a signed-in user to read appConfig,
// so anonymous visitors always stay on the safe (silent) default.
export async function applyConsolePref() {
  if (process.env.NODE_ENV !== 'production') return;
  try {
    const { getFirestore, doc, getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(getFirestore(), 'appConfig', 'global'));
    if (snap.exists() && snap.data()?.flags?.consoleLogsEnabled === true) restore();
  } catch {
    // not signed in / offline / rules deny -> keep the silent default
  }
}
